import { apiClient } from '../../apiHelper';
import {
  ChartDatum,
  CountByDatePoint,
  HomeKpisCoverageItem,
  HomeKpisData,
  HomeKpisSummary,
  MonthlyCountsPoint,
} from '../../../UI/interfaces/homeKpis';
import { useFetch } from '../react-query';
import { AnalyticsQueryKeys } from './analytics-query-keys';

export function useHomeKpis(config = {}) {
  const fetchHomeKpis = async (): Promise<HomeKpisData> => {
    const response = await apiClient.get('/analytics/home-kpis');
    return response.data;
  };

  return useFetch(fetchHomeKpis, AnalyticsQueryKeys.homeKpis, config);
}

export function recordToChartData(
  record: Record<string, number> | undefined
): ChartDatum[] {
  return Object.entries(record || {})
    .filter(([, value]) => value > 0)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

export function topCoverageItems(
  items: HomeKpisCoverageItem[] | undefined,
  limit = 10
): HomeKpisCoverageItem[] {
  return [...(items || [])]
    .sort(
      (a, b) =>
        b.coverage_percent - a.coverage_percent || b.detections - a.detections
    )
    .slice(0, limit);
}

export function latestDailyPoint(
  points: CountByDatePoint[] | undefined
): CountByDatePoint | null {
  return points?.length ? points[points.length - 1] : null;
}

export function previousMonthlyDelta(
  points: MonthlyCountsPoint[] | undefined,
  key: 'patients' | 'studies'
): number | null {
  if (!points || points.length < 2) {
    return null;
  }
  const current = points[points.length - 1][key];
  const previous = points[points.length - 2][key];
  if (!previous) {
    return current ? 100 : 0;
  }
  return Number((((current - previous) / previous) * 100).toFixed(1));
}

export function summaryCards(summary: HomeKpisSummary | undefined) {
  return {
    patients: summary?.total_patients ?? 0,
    exams: summary?.total_studies ?? 0,
    reports: summary?.total_reports ?? 0,
    sweeps: summary?.total_sweeps ?? 0,
    views: summary?.total_views_detected ?? 0,
    examsPerPatient: summary?.exams_per_patient ?? 0,
    viewsPerExam: summary?.views_per_exam ?? 0,
    latestActivityDate: summary?.latest_activity_date ?? null,
  };
}
